import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Download, Loader2, AlertTriangle, Check, ExternalLink, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { FadeIn, Eyebrow } from "@/components/Reveal";
import Seo from "@/components/Seo";
import { api, API } from "@/lib/api";

const STAGES = [
  ["pre_cc", "Before your Construction Certificate"],
  ["pre_commencement", "Before work starts"],
  ["during_construction", "During construction"],
  ["pre_occupation", "Before occupation or completion"],
  ["ongoing", "Ongoing obligations"],
  ["subdivision", "Subdivision steps"],
  ["other", "Other conditions"],
];

const CONDITION_ROWS = [
  ["What you need to do", "action"],
  ["When", "timing"],
  ["Who", "responsible"],
  ["Documents", "documents"],
  ["Where to lodge", "lodgement"],
  ["Payment", "payment"],
  ["Keep as evidence", "evidence"],
];

const Badge = ({ children, tone = "sand" }) => (
  <span className={`text-[10px] font-mono uppercase tracking-[0.1em] px-2 py-0.5 ${tone === "teal" ? "bg-teal text-ivory" : "bg-sand-light text-charcoal/60"}`}>
    {children}
  </span>
);

const DaReport = () => {
  const { id } = useParams();
  const [params] = useSearchParams();
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(false);
  const [done, setDone] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(`pfn_roadmap_${id}`)) || {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    let timer;
    let cancelled = false;
    const load = () =>
      api
        .get(`/roadmap/${id}`)
        .then(async ({ data }) => {
          if (cancelled) return;
          if (data.status === "awaiting_payment" && params.get("session_id")) {
            try {
              const { data: c } = await api.post(`/roadmap/${id}/confirm`, {
                session_id: params.get("session_id"),
              });
              data.status = c.status;
            } catch {
              /* still awaiting payment — show payment state */
            }
          }
          if (cancelled) return;
          setDoc(data);
          if (data.status === "processing") timer = setTimeout(load, 4000);
        })
        .catch(() => !cancelled && setError(true));
    load();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [id]);

  useEffect(() => {
    localStorage.setItem(`pfn_roadmap_${id}`, JSON.stringify(done));
  }, [done, id]);

  if (error) {
    return (
      <div className="py-32 text-center" data-testid="da-report-not-found">
        <h1 className="font-serif text-3xl text-teal">Report not found</h1>
        <Link to="/da-approved" className="mt-4 inline-block text-sm text-teal underline underline-offset-4">
          Back to the service
        </Link>
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="py-32 flex justify-center">
        <Loader2 className="w-8 h-8 text-teal animate-spin" />
      </div>
    );
  }

  if (doc.status === "awaiting_payment") {
    return (
      <div className="py-24" data-testid="da-report-awaiting-payment">
        <div className="max-w-xl mx-auto px-4 text-center">
          <AlertTriangle className="w-10 h-10 text-sand mx-auto" />
          <h1 className="mt-4 font-serif text-3xl text-teal">One step left — payment</h1>
          <p className="mt-4 text-charcoal/70 leading-relaxed">
            Your consent is uploaded safely, but payment wasn't completed. Finish the secure
            checkout (A$69, GST included) and your roadmap starts building immediately.
          </p>
          {doc.checkout_url && (
            <a
              href={doc.checkout_url}
              data-testid="da-resume-checkout"
              className="mt-8 inline-flex items-center gap-2 bg-teal text-ivory text-sm font-medium px-7 py-3.5 rounded-full hover:bg-teal-dark transition-colors"
            >
              Complete payment <ArrowUpRight className="w-4 h-4" />
            </a>
          )}
        </div>
      </div>
    );
  }

  if (doc.status === "processing") {
    return (
      <div className="py-24" data-testid="da-report-processing">
        <div className="max-w-xl mx-auto px-4 text-center">
          <Loader2 className="w-10 h-10 text-teal animate-spin mx-auto" />
          <h1 className="mt-5 font-serif text-3xl text-teal">Reading your consent…</h1>
          <p className="mt-3 text-charcoal/70 leading-relaxed">
            We're extracting every numbered condition, translating it into plain English and
            building your roadmap. This usually takes one to two minutes — this page updates
            automatically.
          </p>
          <div className="mt-8 space-y-2 text-left max-w-sm mx-auto">
            {["Uploading securely", "Extracting your conditions", "Translating into plain English", "Building your roadmap"].map((s, i) => (
              <p key={s} className="text-sm text-charcoal/60 flex items-center gap-3">
                <span className="font-mono text-sand">{String(i + 1).padStart(2, "0")}</span> {s}
              </p>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (doc.status !== "ready") {
    return (
      <div className="py-24" data-testid="da-report-manual-review">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <AlertTriangle className="w-10 h-10 text-sand mx-auto" />
          <h1 className="mt-4 font-serif text-3xl text-teal">We'd rather review this one properly</h1>
          <p className="mt-4 text-charcoal/70 leading-relaxed">{doc.message}</p>
          <Link to="/contact" data-testid="da-manual-contact" className="mt-8 inline-flex items-center gap-2 bg-teal text-ivory text-sm font-medium px-7 py-3.5 rounded-full hover:bg-teal-dark transition-colors">
            Contact us for manual review <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  const r = doc.result;
  const conditions = r.conditions || [];
  const doneCount = conditions.filter((c) => done[c.number]).length;

  return (
    <div data-testid="da-report-page">
      <Seo title="Your Development Roadmap" noindex />
      <section className="bg-sand-light/40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Link to="/da-approved" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.16em] text-charcoal/60 hover:text-teal transition-colors">
            <ArrowLeft className="w-4 h-4" /> After Your DA Is Approved
          </Link>
          <FadeIn>
            <Eyebrow>Your Development Roadmap — What Happens Next</Eyebrow>
            <h1 className="mt-3 font-serif text-3xl sm:text-4xl text-teal tracking-tight">
              {r.address || doc.meta?.address || "Your approved development"}
            </h1>
            <p className="mt-3 text-sm text-charcoal/70">
              {r.council && <span>{r.council} · </span>}
              {r.da_reference && <span>DA {r.da_reference} · </span>}
              {r.approval_date && <span>Approved {r.approval_date} · </span>}
              <span>{conditions.length} conditions identified</span>
            </p>
            {params.get("review") === "1" && (
              <p className="mt-3 text-xs bg-sand-light px-3 py-2 inline-block text-charcoal/70">
                Pilot note: every pilot report receives a quality review before launch of the paid service.
              </p>
            )}
            {params.get("sample") === "1" && (
              <p className="mt-3 text-xs bg-sand-light px-3 py-2 inline-block text-charcoal/70" data-testid="da-sample-note">
                Sample report — based on a fictional NSW consent (14 Banksia Street, Nowra) so you can see exactly what your own roadmap will look like.
              </p>
            )}
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href={`${API}/roadmap/${id}/pdf`}
                data-testid="da-report-download"
                className="inline-flex items-center gap-2 bg-teal text-ivory text-sm font-medium px-6 py-3 rounded-full hover:bg-teal-dark transition-colors"
              >
                <Download className="w-4 h-4" /> Download PDF report
              </a>
              <span className="text-xs font-mono uppercase tracking-[0.12em] text-charcoal/55 self-center" data-testid="da-checklist-progress">
                Checklist: {doneCount}/{conditions.length} marked done
              </span>
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <FadeIn>
            <div className="bg-sand-light/60 border border-cloud p-6" data-testid="da-educational-notice">
              <p className="font-serif text-lg text-teal">Your Educational Development Roadmap</p>
              <div className="mt-2.5 space-y-2 text-xs text-charcoal/75 leading-relaxed">
                <p>
                  This roadmap has been generated using AI-assisted technology and is provided for
                  educational and preliminary planning purposes only. It is not professional planning
                  or legal advice and does not replace your original development consent, approved
                  plans, applicable legislation or directions from the relevant consent authority.
                  The original consent and applicable statutory requirements remain authoritative.
                </p>
                <p>
                  Before relying on any identified next step, confirm the relevant requirements with
                  your council, registered certifier or appropriately qualified professional. Where
                  your conditions depend on plans, reports or certificates you haven't uploaded,
                  that limitation is flagged below.
                </p>
              </div>
            </div>
          </FadeIn>
          <FadeIn>
            <div className="bg-white border border-cloud p-7" data-testid="da-glance">
              <Eyebrow>A. Your approval at a glance</Eyebrow>
              <p className="mt-3 text-charcoal/80 leading-relaxed">{r.summary}</p>
              {r.description && (
                <p className="mt-3 text-sm text-charcoal/65">
                  <span className="font-medium text-teal">Approved development: </span>{r.description}
                </p>
              )}
            </div>
          </FadeIn>

          {r.next_steps?.length > 0 && (
            <FadeIn>
              <div className="bg-teal text-ivory p-7" data-testid="da-next-steps">
                <Eyebrow>B. Your immediate next steps</Eyebrow>
                <ol className="mt-4 space-y-3">
                  {r.next_steps.map((s, i) => (
                    <li key={i} className="flex gap-3 text-sm leading-relaxed text-ivory/90">
                      <span className="font-serif text-sand shrink-0">{String(i + 1).padStart(2, "0")}</span>
                      {s}
                    </li>
                  ))}
                </ol>
              </div>
            </FadeIn>
          )}

          <div data-testid="da-conditions">
            <Eyebrow>C &amp; F. Understanding your conditions — and your checklist</Eyebrow>
            <p className="mt-2 mb-6 text-xs text-charcoal/60 leading-relaxed">
              Tick a condition as you complete it — this is your personal record, not formal
              confirmation that a condition has been legally satisfied.
            </p>
            {STAGES.map(([stage, label]) => {
              const conds = conditions.filter((c) => c.stage === stage);
              if (!conds.length) return null;
              return (
                <div key={stage} className="mb-8">
                  <h2 className="font-serif text-xl text-teal border-b border-cloud pb-2">{label}</h2>
                  <div className="mt-4 space-y-4">
                    {conds.map((c) => (
                      <FadeIn key={c.number}>
                        <div className={`bg-white border p-6 transition-colors ${done[c.number] ? "border-seaglass bg-seaglass-light/30" : "border-cloud"}`} data-testid={`da-condition-${c.number}`}>
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex items-start gap-3">
                              <button
                                onClick={() => setDone((d) => ({ ...d, [c.number]: !d[c.number] }))}
                                data-testid={`da-condition-check-${c.number}`}
                                aria-label={`Mark condition ${c.number} done`}
                                className={`mt-0.5 w-5 h-5 border shrink-0 flex items-center justify-center transition-colors ${
                                  done[c.number] ? "bg-teal border-teal" : "border-cloud bg-white"
                                }`}
                              >
                                {done[c.number] && <Check className="w-3.5 h-3.5 text-ivory" />}
                              </button>
                              <div>
                                <p className="font-medium text-teal">
                                  Condition {c.number}
                                  <span className="ml-2 text-xs font-mono text-charcoal/50">page {c.page}</span>
                                </p>
                                <div className="mt-1.5 flex flex-wrap gap-1.5">
                                  <Badge tone="teal">{c.kind === "statutory" ? "Statutory" : c.kind === "council" ? "Council" : "Good practice"}</Badge>
                                  {c.needs_professional && <Badge>Professional required</Badge>}
                                  {c.unverified && <Badge>Verify with council</Badge>}
                                </div>
                              </div>
                            </div>
                          </div>
                          {c.original && (
                            <p className="mt-3 text-xs text-charcoal/55 italic leading-relaxed border-l-2 border-sand pl-3">
                              "{c.original}"
                            </p>
                          )}
                          <p className="mt-3 text-sm text-charcoal/85 leading-relaxed">{c.plain}</p>
                          <dl className="mt-3 space-y-1.5">
                            {CONDITION_ROWS.map(([lbl, k]) =>
                              c[k] ? (
                                <div key={k} className="flex gap-2 text-xs leading-relaxed">
                                  <dt className="w-28 shrink-0 font-medium text-charcoal/55">{lbl}</dt>
                                  <dd className="text-charcoal/80">{c[k]}</dd>
                                </div>
                              ) : null
                            )}
                          </dl>
                        </div>
                      </FadeIn>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {r.applications?.length > 0 && (
            <FadeIn>
              <div data-testid="da-applications">
                <Eyebrow>D &amp; E. Your required applications — and how to lodge them</Eyebrow>
                <div className="mt-5 space-y-4">
                  {r.applications.map((a, i) => (
                    <div key={i} className="bg-white border border-cloud p-6">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-serif text-lg text-teal">{a.name}</p>
                        {a.unverified && <Badge>Verify with council</Badge>}
                      </div>
                      {a.why && <p className="mt-2 text-sm text-charcoal/80 leading-relaxed">{a.why}</p>}
                      <dl className="mt-3 space-y-1.5 text-xs leading-relaxed">
                        {a.authority && <div className="flex gap-2"><dt className="w-28 shrink-0 font-medium text-charcoal/55">Authority</dt><dd className="text-charcoal/80">{a.authority}</dd></div>}
                        {a.lodgement && <div className="flex gap-2"><dt className="w-28 shrink-0 font-medium text-charcoal/55">How to lodge</dt><dd className="text-charcoal/80">{a.lodgement}</dd></div>}
                        {a.fees && <div className="flex gap-2"><dt className="w-28 shrink-0 font-medium text-charcoal/55">Fees</dt><dd className="text-charcoal/80">{a.fees}</dd></div>}
                      </dl>
                      {a.steps?.length > 0 && (
                        <ol className="mt-3 space-y-1.5">
                          {a.steps.map((s, j) => (
                            <li key={j} className="flex gap-2.5 text-xs text-charcoal/80 leading-relaxed">
                              <span className="font-mono text-teal shrink-0">{j + 1}.</span>{s}
                            </li>
                          ))}
                        </ol>
                      )}
                      {a.documents?.length > 0 && (
                        <p className="mt-3 text-xs text-charcoal/70"><span className="font-medium">Prepare: </span>{a.documents.join(", ")}</p>
                      )}
                      {a.link && (
                        <a href={a.link} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.12em] text-teal underline underline-offset-4">
                          Official page <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </FadeIn>
          )}

          {(r.flags?.length > 0 || r.missing_documents?.length > 0) && (
            <FadeIn>
              <div className="bg-sand-light/50 border border-cloud p-6" data-testid="da-flags">
                <Eyebrow>G. Flags &amp; documents to gather</Eyebrow>
                {r.flags?.map((f, i) => (
                  <p key={i} className="mt-3 flex gap-2.5 text-sm text-charcoal/80 leading-relaxed">
                    <AlertTriangle className="w-4 h-4 text-teal shrink-0 mt-0.5" /> {f}
                  </p>
                ))}
                {r.missing_documents?.map((m, i) => (
                  <p key={i} className="mt-2 text-sm text-charcoal/80">• Gather: {m}</p>
                ))}
              </div>
            </FadeIn>
          )}

          <FadeIn>
            <p className="text-xs text-charcoal/55 leading-relaxed max-w-2xl">
              PLAN FIRST NSW provides independent planning education and AI-assisted guidance —
              not statutory approval, certification or legal advice. This report does not confirm
              that every condition has been identified, that a statutory requirement has been
              satisfied, or that work can lawfully commence. Always verify current requirements,
              fees and forms with your council or certifier. Marking a task complete here is your
              personal record, not legal confirmation. You can ask us to delete your documents and
              report at any time via hello@planfirstnsw.com.au.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/book" data-testid="da-report-cta-book" className="inline-flex items-center gap-2 bg-teal text-ivory text-sm font-medium px-6 py-3 rounded-full hover:bg-teal-dark transition-colors">
                Need a professional? Planner Connect — coming soon <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link to="/da-approved" className="inline-flex items-center gap-2 border border-teal text-teal text-sm font-medium px-6 py-3 rounded-full hover:bg-teal hover:text-ivory transition-colors">
                Analyse another consent
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

export default DaReport;
